/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react';
import Media from 'react-media';
import { object, string, number, arrayOf, PropTypes, bool } from 'prop-types';
import { useSelector } from 'react-redux';

// MUI
import { Grid, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

// Components
import DesktopRecipientBookList from './DesktopRecipientBookList';
import SortRecipientBook from '../Common/SortRecipientBook/SortRecipientBook';
import FilterRecipientBookModal from '../Common/FilterRecipientBook/FilterRecipientBookModal';
import SearchFilter from '../Common/FilterRecipientBook/SearchFilter';
import ErrorPage from '../../../../common/ErrorPage';
import RecipientBookBanner from '../Common/RecipientBookBanner';

// Styles
import { RecipientBookStyle } from '../Common/Assets/Style/RecipientBookStyle';

// Helpers
import { combineStyle } from '../../../../style/combineStyle';
import configENV from '../../../../config';
import { deepObjectExtend } from '../../../../utils/object';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { RecipientBookContext } from '../Common/Context/RecipientBookContext';
import DesktopAddNewRecipient from '../Common/AddNewRecipient/DesktopAddNewRecipient';
import { isEmpty } from '../../../../../../pages/Account/utils/object';
import { useDispatch } from 'react-redux';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import PageTitle from '../../../../common/PageTitle';
import { maskPhoneNumber } from '../Common/Helpers/Helpers';
import CUSTOM_BREAKPOINTS from '../../../../style/customBreakpoints';
import { getAppRoutePrefix } from '../../../../../../helpers/common/helper';
// import StyledButton from '../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        RecipientBookStyle,
    }),
);

const { RECIPIENTS_PER_PAGE } = configENV;
const DesktopRecipientBook = ({ recipients, recipientBookListResError, classes }) => {
    const { recipientState, setRecipientState, currentRecipient, filteredRecipients, setFilteredRecipients, setRecipientsCopy } = useContext(RecipientBookContext);
    const [isViewingAddNewRecipientForm, setIsViewingAddNewRecipientForm] = useState(false);
    const [isEditingRecipient, setIsEditingRecipient] = useState(false);
    const [visibleRecipientsCount, setVisibleRecipientsCount] = useState(RECIPIENTS_PER_PAGE);
    const isFilterEnabled = useSelector(getFeatureFlag('is-recipient-book-filter-on'));
    const isSortingEnabled = useSelector(getFeatureFlag('is-recipient-book-sorting-on'));
    const isRecipientBookBannerOn = useSelector(getFeatureFlag('is-recipient-book-banner-on'));
    const isLazyLoadingEnabled = useSelector(getFeatureFlag('is-account-contacts-lazy-loading-enabled'));

    // Flag controlling which page we show the weather banner on
    const pageToHideHeatherBannerOnFlag = useSelector(getFeatureFlag('pages-to-hide-weather-banner-on'));
    // We show the banner on the Recipient Book page in Prod, and only show during bad weather
    // The banner itself has a 15px margin
    // So we need to add a margin top above the recipient book to add sufficient spacing when the banner is turned OFF
    const isShowingWeatherBanner = pageToHideHeatherBannerOnFlag[window?.location?.pathname] || false;

    const dispatch = useDispatch();
    const breakpointExpr = `(max-width: ${(CUSTOM_BREAKPOINTS.tabLandscape)}px)`;

    const totalContacts = !isEmpty(filteredRecipients) ? filteredRecipients.addressBookResponse?.addressList?.length : recipientState?.addressBookResponse?.addressList?.length || 0;
    const contactsLabel = `${totalContacts} contact${totalContacts !== 1 ? 's' : ''}`;
    const selfContactCardCount = (filteredRecipients?.addressBookResponse ? filteredRecipients : recipientState)?.addressBookResponse?.addressList?.filter((address) => +address.relationship === 0)?.length;

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    }, [recipientState])

    useEffect(() => {
        if (recipients?.addressBookResponse?.addressList?.length >= 0) {
            const updatedData = { ...recipients };
            updatedData.addressBookResponse.addressList = updatedData?.addressBookResponse?.addressList?.map((recipient) => {
                if (recipient.occasions?.length) {
                    recipient.occasions = recipient.occasions.map((occ) => {
                        if (occ.reminderPhone?.length) {
                            occ.reminderPhone = maskPhoneNumber(updatedData.addressBookResponse.reminderPhone || '');
                        } else {
                            occ['reminderPhone'] = maskPhoneNumber(updatedData.addressBookResponse.reminderPhone || '');
                        }
                        return occ;
                    })
                }
                return recipient;
            });
            setRecipientState(updatedData);
            setRecipientsCopy(updatedData);
        }
    }, [recipients]);

    useEffect(() => {
        if (recipientBookListResError === 'NO_RECIPIENTS_FOUND') {
            setRecipientState({
                addressBookResponse: {
                    addressList: []
                }
            });
        }
    }, [recipientBookListResError]);

    const lazyLoadContacts = () => setVisibleRecipientsCount(prevVisibleRecipientsCount => prevVisibleRecipientsCount + RECIPIENTS_PER_PAGE);
    const resetVisibleRecipientsCount = () => (visibleRecipientsCount !== RECIPIENTS_PER_PAGE && setVisibleRecipientsCount(RECIPIENTS_PER_PAGE));

    if (recipientBookListResError === 'GENERIC_API_ERROR') {
        return (
            <Media query={breakpointExpr}>
                {(matches) => (
                    matches ? (
                        <div className={classes.container}>
                            <div className={classes.title}>Contacts</div>
                            <ErrorPage />
                        </div>
                    ) : (
                        <>
                            <Grid item xs={10} style={{ padding: '20px 11px 0 43px' }}>
                                <div className={classes.container}>
                                    <div className={classes.title}>Contacts</div>
                                    <ErrorPage />
                                </div>
                            </Grid>
                        </>
                    )
                )}
            </Media>
        );
    }

    // Show Load More button:
    // case 1: Filtered Recipients result from search box have more than 10 contacts to view more
    // case 2: Filtered Recipients result from search box OR recient state has more than 10 contacts
    // case 3: Filtered recipients are empty (empty search box) AND there are more than 10 contacts

    const filteredContactsLength = filteredRecipients?.addressBookResponse?.addressList?.length ?? 0;
    const contactListLength = recipientState?.addressBookResponse?.addressList?.length ?? 0;
    const hasFilteredResponse = !!filteredRecipients?.addressBookResponse;
    
    const isShowingLoadMoreBtn =
        (filteredContactsLength > 10 && visibleRecipientsCount <= filteredContactsLength) ||
        (filteredContactsLength > 10 && contactListLength > 10 && visibleRecipientsCount <= contactListLength) ||
        (!hasFilteredResponse && contactListLength > 10 && visibleRecipientsCount <= contactListLength);

    return (
        <Grid style={{ width: '74.8%', margin: '0px 3% 0px 5%', marginTop: isShowingWeatherBanner ? '0px' : '15px' }}>
            {isViewingAddNewRecipientForm ?
                <DesktopAddNewRecipient
                    classes={classes}
                    recipientState={recipientState}
                    setRecipientState={setRecipientState}
                    setIsViewingAddNewRecipientForm={setIsViewingAddNewRecipientForm}
                /> :
                (!isEditingRecipient && isEmpty(currentRecipient)) && (
                    <>
                        {isRecipientBookBannerOn && <RecipientBookBanner queryTitle="Address Book Banner" />}
                        <Grid container item xs={12} justifyContent='space-between' alignItems='center' style={{ margin: '40px 0px' }}>
                            <PageTitle title="Contacts" />
                            <Grid item>
                                <Button
                                    fullWidth
                                    variant='contained'
                                    className={classes.commonBtnContained}
                                    style={{ width: '236px', margin: '0' }}
                                    onClick={() => {
                                        dispatch(trackEvent({
                                            // everything starting with `event` needs to be camelCase
                                            eventName: "address_book_interaction",
                                            interaction_type: "add_contact_start",
                                            eventCategory: "Address Book",
                                            eventAction: "Interaction",
                                            eventLabel: "Add Contact Start"
                                        }))
                                        setIsViewingAddNewRecipientForm(true);
                                        window.history.pushState('', '', getAppRoutePrefix()+'/account/address-book/add-new-recipient');
                                    }}
                                    aria-labelledby='add new contact'
                                >
                                    Add New Contact
                                </Button>
                                {
                                    /*
                                        This EDS button styles are being overriden by the button styles inside main.js

                                        We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                        is not protected. Disabling this button for now.
                                    */
                                }
                                {/* <StyledButton
                                    fullWidth
                                    variant='primary'
                                    label='Add New Contact'
                                    handleOnClick={() => {
                                        dispatch(trackEvent({
                                            // everything starting with `event` needs to be camelCase
                                            eventName: "address_book_interaction",
                                            interaction_type: "add_contact_start",
                                            eventCategory: "Address Book",
                                            eventAction: "Interaction",
                                            eventLabel: "Add Contact Start"
                                        }))
                                        setIsViewingAddNewRecipientForm(true);
                                        window.history.pushState('', '', '/account/address-book/add-new-recipient');
                                    }}
                                /> */}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} className={classes.title}>
                            <div className={classes.searchField}>
                                <SearchFilter recipientState={recipientState} setFilteredRecipients={setFilteredRecipients} resetVisibleRecipientsCount={resetVisibleRecipientsCount} />
                            </div>
                        </Grid>
                        <Grid className={classes.filterAndSortContainer}>
                            {isFilterEnabled && <FilterRecipientBookModal recipientState={recipientState} classes={classes} />}
                            {isSortingEnabled && <SortRecipientBook recipientState={recipientState} setRecipientState={setRecipientState} classes={classes} />}
                        </Grid>
                        <Grid container id='desktopRecipientBookContacts' className={classes.listHeading}>
                            {
                                selfContactCardCount
                                    ? (
                                        <Grid item>
                                            <p>My Contact Card{selfContactCardCount > 1 ? 's' : ''}</p>
                                        </Grid>
                                    ) : ''
                            }
                            <Grid item style={{ marginLeft: 'auto' }}>
                                <p className={classes.totalContacts}>{contactsLabel}</p>
                            </Grid>
                        </Grid>
                        <DesktopRecipientBookList
                            classes={classes}
                            lazyLoadContacts={isLazyLoadingEnabled ? lazyLoadContacts : null}
                            recipientState={
                                filteredRecipients?.addressBookResponse
                                    ? filteredRecipients
                                    : recipientState
                            }
                            setIsEditingRecipient={setIsEditingRecipient}
                            visibleRecipientsCount={visibleRecipientsCount}
                        />
                        {
                            isShowingLoadMoreBtn && !isLazyLoadingEnabled && (
                                <Grid container justifyContent="center" style={{ marginTop: '48px', marginBottom: '80px' }}>
                                    <Button
                                        style={{ height: '46px', width: '130px', padding: '11px 24px', borderRadius: '0px' }}
                                        fullWidth
                                        className={`${classes.outlinedPrimaryBtn} text-transform-none`}
                                        onClick={() => {
                                            setVisibleRecipientsCount(prevVisibleRecipientsCount => prevVisibleRecipientsCount + RECIPIENTS_PER_PAGE);
                                        }}
                                    >
                                        Load More
                                    </Button>
                                </Grid>
                            )
                        }
                    </>
                )
            }
        </Grid>
    );
};

DesktopRecipientBook.propTypes = {
    recipients: PropTypes.shape({
        addressBookResponse: PropTypes.shape({
            addressList: arrayOf(PropTypes.shape({
                addresses: arrayOf(PropTypes.shape({
                    addressBookEntryId: string,
                    addressLineOne: string,
                    addressType: string,
                    city: string,
                    country: string,
                    firstName: string,
                    isBillingAddress: bool,
                    lastName: string,
                    lastUpdateDate: string,
                    phoneNumber: string,
                    rcpId: string,
                    state: string,
                    zip: string,
                })),
                occasions: arrayOf(PropTypes.shape({
                    desc: string,
                    rcpId: string,
                    dateDD: string,
                    dateMM: string,
                    dateYYYY: string,
                    subscribed: bool,
                    occasion: string,
                    occasionId: string,
                    createdDate: string,
                    createdUser: string,
                    reminderEmail: string,
                    reminderPhone: string,
                    subscribedEmail: bool,
                    subscribedPhone: bool,
                })),
                rcpAddressBookEntryId: string,
                rcpFirstName: string,
                rcpId: string,
                rcpLastName: string,
            })),
            contactId: string,
            reminderEmail: string,
            totalEntries: number,
        }),
    }),
    recipientBookListResError: string.isRequired,
    classes: object.isRequired,
};

export default withStyles(styles)(DesktopRecipientBook);
